import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, BadgePercent, Tags, TrendingUp, Zap, FileCheck2, Building2 } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import { api, track, submitLead } from "../../lib/api";

const BENEFITS = [
  { icon: BadgePercent, h: "Discounted Flight Fares", p: "Get access to exclusive discounted airfares to maximize your earnings." },
  { icon: Tags, h: "Sell your own fares and deals", p: "Upload and offer your own private fares alongside Sellvago's options." },
  { icon: TrendingUp, h: "Set your own profit margins", p: "Add your own markup on every booking and control how much you earn." },
  { icon: Zap, h: "Easy setup and quick support", p: "Get started in minutes with responsive support always ready to help." },
  { icon: FileCheck2, h: "No license required", p: "Start selling flight tickets instantly without needing any travel license." },
  { icon: Building2, h: "Maintain your corporate identity", p: "Keep your agency's branding consistent across all bookings and invoices." },
];

export default function Signup() {
  usePageMeta(
    "Sign Up — Sellvago Partner Registration",
    "Register with Sellvago to access exclusive discounted fares, sell your own deals and manage your travel business effortlessly."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", module: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.module) {
      toast.error("Name, email and module are required");
      return;
    }
    setSending(true);
    try {
      await submitLead({
        lead_type: "b2b_signup",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `Module: ${form.module}`,
        source_page: "/signup",
      });
      track("lead_capture", "/signup", { module: form.module });
      setSent(true);
      toast.success("Registration received! Our team will activate your account shortly.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const cls = "w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#970C10] transition-colors duration-200";

  return (
    <div className="pt-36 pb-16 px-6 lg:px-10" data-testid="signup-page">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-4">Partner Registration</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white">Welcome!</h1>
          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
            Register now to access your bookings, exclusive fares, and manage your travel business effortlessly.
          </p>
          <ul className="mt-10 space-y-4">
            {["Access exclusive fares and grow your travel business", "Instant ticketing once your account is activated", "Dedicated onboarding within one business day"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-[#E8767A] shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-dark rounded-sm p-8 h-fit">
          {sent ? (
            <div className="py-16 text-center" data-testid="signup-success">
              <h2 className="font-heading text-2xl font-bold text-white">Registration received.</h2>
              <p className="mt-3 text-slate-400 text-sm">
                Our team will contact you at {form.email} to activate your {form.module} account.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
              <input data-testid="signup-name-input" className={cls} placeholder="Name *" value={form.name} onChange={set("name")} />
              <input data-testid="signup-email-input" className={cls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="signup-phone-input" className={cls} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} />
              <select data-testid="signup-module-select" className={cls} value={form.module} onChange={set("module")}>
                <option value="" className="bg-[#032347]">- - Select Module - -</option>
                {["Essential", "Flex", "Elite"].map((m) => (
                  <option key={m} value={m} className="bg-[#032347]">{m}</option>
                ))}
              </select>
              <button
                data-testid="signup-submit-btn"
                disabled={sending}
                className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-3.5 rounded-sm transition-colors duration-200 disabled:opacity-50"
              >
                {sending ? "Registering..." : "Register Now"}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto mt-24">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white max-w-xl">
          Manage your travel business effortlessly.
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.h} className="glass-dark rounded-sm p-7" data-testid={`signup-benefit-${b.h.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <b.icon className="w-6 h-6 text-[#E8767A] mb-4" />
              <h3 className="font-heading font-bold text-white text-base">{b.h}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.p}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
